import { useEffect, useMemo, useState } from 'react';
import { SectionTitle, Badge, CommandPreview, RunButton } from '../UI';

const COMMANDS = [
  { cmd: '/geo audit', desc: 'Full structured GEO audit with client-ready report artifacts', badge: 'deep' },
  { cmd: '/geo quick', desc: 'Fast GEO snapshot with preserved history', badge: 'fast' },
  { cmd: '/geo citability', desc: 'Score content for AI citation readiness', badge: 'deep' },
  { cmd: '/geo crawlers', desc: 'Check AI crawler access via robots.txt', badge: 'fast' },
  { cmd: '/geo llmstxt', desc: 'Analyze llms.txt readiness and guidance gaps', badge: 'generate' },
  { cmd: '/geo brands', desc: 'Scan brand mention and entity visibility signals', badge: 'deep' },
  { cmd: '/geo platforms', desc: 'Platform-specific optimization per AI engine', badge: 'deep' },
  { cmd: '/geo schema', desc: 'Detect, validate, and generate structured data guidance', badge: 'generate' },
  { cmd: '/geo technical', desc: 'Technical SEO audit with AI visibility impact', badge: 'deep' },
  { cmd: '/geo content', desc: 'Content quality and E-E-A-T assessment', badge: 'deep' },
  { cmd: '/geo report', desc: 'Generate original-style structured markdown report', badge: 'generate' },
  { cmd: '/geo report-pdf', desc: 'Generate the real PDF report artifact', badge: 'generate' },
];

export default function CommandsTab({
  url,
  config,
  jobs,
  onSaveProject,
  onLogRun,
  onExecuteRun,
  onExecuteBatch,
  onRefreshProjects,
  onRefreshJobs,
}) {
  const [selected, setSelected] = useState('/geo audit');
  const [projectName, setProjectName] = useState('');
  const [geoScore, setGeoScore] = useState('');
  const [summary, setSummary] = useState('');
  const [projectStatus, setProjectStatus] = useState('audit');
  const [notice, setNotice] = useState('');
  const [batchText, setBatchText] = useState('');
  const [result, setResult] = useState(null);
  const [activeJobId, setActiveJobId] = useState(null);

  const fullCmd = `${selected} ${url}`;
  const domain = useMemo(() => {
    try {
      return new URL(url).hostname;
    } catch {
      return url.replace(/^https?:\/\//, '').split('/')[0];
    }
  }, [url]);

  const domainJobs = useMemo(
    () => jobs.filter((job) => job.domain === domain).slice().sort((a, b) => new Date(b.created_at) - new Date(a.created_at)),
    [jobs, domain]
  );
  const activeJob = domainJobs.find((job) => job.id === activeJobId) || domainJobs[0] || null;

  useEffect(() => {
    if (!activeJob) {
      return;
    }
    if (activeJob.status === 'completed' && activeJob.result) {
      setResult(activeJob.result);
      setGeoScore(String(activeJob.result.geo_score ?? ''));
      setSummary(activeJob.result.summary || '');
      setNotice('Job completed. Artifacts are ready below.');
      onRefreshProjects?.();
    } else if (activeJob.status === 'failed') {
      setNotice(activeJob.error || 'Job failed.');
    }
  }, [activeJob, onRefreshProjects]);

  async function copyCmd() {
    await navigator.clipboard?.writeText(fullCmd);
    setNotice('Command copied.');
    setTimeout(() => setNotice(''), 1500);
  }

  async function saveRun() {
    if (!domain) {
      setNotice('Enter a valid URL first.');
      return;
    }

    const payload = {
      domain,
      homepage_url: url,
      name: projectName || domain,
      project_status: projectStatus,
      command: selected,
      model: config.default_model,
      status: 'completed',
      geo_score: geoScore ? Number(geoScore) : null,
      summary,
      snapshot: {
        source: 'manual-panel-log',
        command_preview: fullCmd,
      },
    };

    await onSaveProject({
      domain,
      homepage_url: url,
      name: projectName || domain,
      status: projectStatus,
      metadata: { last_command: selected },
    });
    await onLogRun(payload);
    setNotice('Run saved to project history.');
    setTimeout(() => setNotice(''), 2000);
  }

  async function executeRun() {
    if (!domain) {
      setNotice('Enter a valid URL first.');
      return;
    }
    setNotice('Queueing job...');
    const job = await onExecuteRun({
      url,
      domain,
      name: projectName || domain,
      command: selected,
      model: config.default_model,
      project_status: projectStatus,
    });
    setActiveJobId(job.id);
    setNotice(`Job #${job.id} queued.`);
  }

  async function executeBatch() {
    const urls = batchText
      .split('\n')
      .map((line) => line.trim())
      .filter(Boolean);
    if (!urls.length) {
      setNotice('Add one URL per line to batch queue.');
      return;
    }
    const payload = {
      items: urls.map((item) => ({
        url: item,
        command: selected,
        model: config.default_model,
        project_status: projectStatus,
      })),
    };
    const response = await onExecuteBatch(payload);
    setNotice(`Queued ${response.jobs.length} jobs in batch ${response.batch_id.slice(0, 8)}.`);
    setBatchText('');
    onRefreshJobs?.();
  }

  return (
    <div>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '24px', fontWeight: '700', marginBottom: '6px' }}>Commands</h1>
        <p style={{ fontSize: '14px', color: 'var(--text-3)' }}>
          Queue structured GEO jobs, generate original-style reports, and build PDFs without blocking the dashboard.
        </p>
      </div>

      <SectionTitle>Available commands</SectionTitle>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px', marginBottom: '24px' }}>
        {COMMANDS.map((item) => (
          <button
            key={item.cmd}
            onClick={() => setSelected(item.cmd)}
            style={{
              background: 'var(--bg-card)',
              border: selected === item.cmd ? '1px solid var(--accent)' : '1px solid var(--border)',
              borderRadius: '12px',
              padding: '14px 16px',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <div style={{ fontSize: '13px', fontFamily: 'var(--font-mono)', color: selected === item.cmd ? 'var(--accent)' : 'var(--text-1)', marginBottom: '6px' }}>{item.cmd}</div>
            <div style={{ fontSize: '12px', color: 'var(--text-3)', lineHeight: 1.5, marginBottom: '8px' }}>{item.desc}</div>
            <Badge type={item.badge}>{item.badge}</Badge>
          </button>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1.15fr 0.85fr', gap: '18px' }}>
        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
          <SectionTitle>Command preview</SectionTitle>
          <CommandPreview cmd={selected} url={url} />
          <div style={{ marginTop: '12px', color: 'var(--text-3)', fontSize: '12px', fontFamily: 'var(--font-mono)' }}>Default model: {config.default_model}</div>
          <div style={{ display: 'grid', gap: '10px', marginTop: '16px' }}>
            <RunButton full onClick={copyCmd}>Copy Command</RunButton>
            <RunButton full onClick={executeRun}>Queue Structured Run</RunButton>
          </div>
          <div style={{ marginTop: '20px' }}>
            <SectionTitle>Batch queue</SectionTitle>
            <textarea
              value={batchText}
              onChange={(e) => setBatchText(e.target.value)}
              placeholder={'https://example.com\nhttps://example.org'}
              style={{ ...inputStyle, minHeight: '120px', resize: 'vertical' }}
            />
            <div style={{ marginTop: '10px' }}>
              <RunButton full secondary onClick={executeBatch}>Queue Batch</RunButton>
            </div>
          </div>
        </div>

        <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px', display: 'grid', gap: '14px' }}>
          <SectionTitle>Project metadata</SectionTitle>
          <Field label="Project name">
            <input value={projectName} onChange={(e) => setProjectName(e.target.value)} placeholder={domain || 'acme.com'} style={inputStyle} />
          </Field>
          <Field label="Project status">
            <select value={projectStatus} onChange={(e) => setProjectStatus(e.target.value)} style={inputStyle}>
              {['lead', 'audit', 'proposal', 'active', 'churned', 'lost'].map((status) => <option key={status} value={status}>{status}</option>)}
            </select>
          </Field>
          <Field label="Latest GEO score">
            <input value={geoScore} onChange={(e) => setGeoScore(e.target.value)} placeholder="72" style={inputStyle} />
          </Field>
          <Field label="Run summary">
            <textarea value={summary} onChange={(e) => setSummary(e.target.value)} placeholder="Audit found weak schema coverage and missing llms.txt..." style={{ ...inputStyle, minHeight: '110px', resize: 'vertical' }} />
          </Field>
          {notice && <div style={{ color: notice.toLowerCase().includes('fail') ? 'var(--poor)' : 'var(--good)', fontFamily: 'var(--font-mono)', fontSize: '12px' }}>{notice}</div>}
          <RunButton full secondary onClick={saveRun}>Save Manual Run To Project</RunButton>
        </div>
      </div>

      {activeJob && (
        <div style={{ marginTop: '24px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
          <SectionTitle>Active or latest domain job</SectionTitle>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <div>
              <div style={{ fontFamily: 'var(--font-mono)', color: 'var(--accent)', fontSize: '13px', marginBottom: '6px' }}>{activeJob.command} {activeJob.url}</div>
              <Badge type={activeJob.status === 'completed' ? 'fast' : activeJob.status === 'failed' ? 'generate' : 'deep'}>{activeJob.status}</Badge>
            </div>
            <div style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-3)' }}>
              <div>Job #{activeJob.id}</div>
              <div>{activeJob.progress}%</div>
            </div>
          </div>
          <div style={{ height: '6px', background: 'var(--bg-surface)', borderRadius: '999px', overflow: 'hidden', marginBottom: '10px' }}>
            <div style={{ width: `${activeJob.progress}%`, height: '100%', background: activeJob.status === 'failed' ? 'var(--poor)' : 'var(--accent)', transition: 'width 0.3s ease' }} />
          </div>
          <div style={{ color: 'var(--text-2)', fontSize: '13px', marginBottom: '12px' }}>{activeJob.error || activeJob.message || 'Queued'}</div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {activeJob.artifacts?.report_url && <a href={activeJob.artifacts.report_url} style={linkStyle}>Download Report</a>}
            {activeJob.artifacts?.pdf_url && <a href={activeJob.artifacts.pdf_url} style={linkStyle}>Download PDF</a>}
            {activeJob.artifacts?.data_url && <a href={activeJob.artifacts.data_url} style={linkStyle}>Download Data</a>}
          </div>
        </div>
      )}

      {result && (
        <div style={{ marginTop: '24px', background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '12px', padding: '20px' }}>
          <SectionTitle>Latest generated result</SectionTitle>
          <div style={{ display: 'flex', gap: '18px', alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: '16px' }}>
            <div style={{ minWidth: '120px' }}>
              <div style={{ fontSize: '40px', fontWeight: '700', fontFamily: 'var(--font-display)', color: 'var(--accent)' }}>{result.geo_score}</div>
              <div style={{ fontSize: '12px', color: 'var(--text-3)', fontFamily: 'var(--font-mono)' }}>GEO score</div>
            </div>
            <div style={{ flex: 1, minWidth: '280px' }}>
              <div style={{ fontSize: '14px', color: 'var(--text-2)', lineHeight: 1.7, marginBottom: '12px' }}>{result.summary}</div>
              <div style={{ display: 'grid', gap: '6px' }}>
                {(result.key_findings || []).map((item, index) => (
                  <div key={`${item}-${index}`} style={{ color: 'var(--text-3)', fontSize: '13px' }}>- {item}</div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '16px', whiteSpace: 'pre-wrap', color: 'var(--text-2)', fontSize: '13px', lineHeight: 1.7, maxHeight: '60vh', overflow: 'auto' }}>
            {result.report_markdown}
          </div>
        </div>
      )}

      <div style={{ marginTop: '24px' }}>
        <SectionTitle>Recent jobs for this domain</SectionTitle>
        <div style={{ display: 'grid', gap: '10px' }}>
          {domainJobs.slice(0, 5).map((job) => (
            <div key={job.id} style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 16px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '8px' }}>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '13px', color: 'var(--accent)' }}>#{job.id} {job.command}</div>
                <div style={{ fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--text-3)' }}>{job.status} · {job.progress}%</div>
              </div>
              <div style={{ fontSize: '13px', color: 'var(--text-2)', marginBottom: '8px' }}>{job.error || job.message || 'Queued'}</div>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {job.artifacts?.report_url && <a href={job.artifacts.report_url} style={linkStyle}>Report</a>}
                {job.artifacts?.pdf_url && <a href={job.artifacts.pdf_url} style={linkStyle}>PDF</a>}
                {job.artifacts?.data_url && <a href={job.artifacts.data_url} style={linkStyle}>Data</a>}
              </div>
            </div>
          ))}
          {domainJobs.length === 0 && <div style={{ color: 'var(--text-3)' }}>No queued or completed jobs for this domain yet.</div>}
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: 'grid', gap: '8px' }}>
      <span style={{ fontSize: '11px', color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '1px', fontFamily: 'var(--font-mono)' }}>{label}</span>
      {children}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '12px 14px',
  background: 'var(--bg-surface)',
  border: '1px solid var(--border)',
  borderRadius: '10px',
  color: 'var(--text-1)',
  fontSize: '14px',
  outline: 'none',
};

const linkStyle = {
  color: 'var(--accent)',
  textDecoration: 'none',
  fontSize: '12px',
  fontFamily: 'var(--font-mono)',
};
